import { isAfter } from 'date-fns'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useMemo, useState } from 'react'

import { Badge, Copy, Flex, Icon, Menu, SectionProps, TableCell, TableRow } from '@percent/lemonade'
import { getFormattedDate } from '@percent/partner-dashboard/common/library/utility/date'
import { PartnerApiKey } from '@percent/partner-dashboard/context/auth/authContext/AuthContext.types'
import styles from './KeyList.module.scss'
import { ExpireKeySetModal } from './modals/ExpireKeySetModal'
import { RenameKeySetModal } from './modals/RenameKeySetModal'

type KeyRowProps = {
  apiKey: PartnerApiKey
  refetchApiKeys: () => void
}

const getTruncatedKey = (key: string, type: string) => {
  if (type !== 'secret') return key

  return `${key.substring(0, key.indexOf('sk_') + 7)}${'*'.repeat(12)}`
}

export function KeyRow({ apiKey, refetchApiKeys }: KeyRowProps) {
  const { t } = useTranslation()
  const isExpired = apiKey.expiresAt ? isAfter(new Date(), new Date(apiKey.expiresAt)) : false
  const badgeVariant = isExpired ? 'default' : 'positive'
  const badgeText = isExpired ? 'Expired' : 'Live'
  const [modalTypeOpen, setModalTypeOpen] = useState<'expire' | 'rename' | null>(null)

  const displayKey = useMemo(() => {
    return getTruncatedKey(apiKey.value, apiKey.type)
  }, [apiKey.value, apiKey.type])

  const menuSections: SectionProps[] = [
    {
      items: [
        { key: 'expire', label: t('typography.expireKeySet'), onClick: () => setModalTypeOpen('expire') },
        { key: 'rename', label: t('typography.renameKeySet'), onClick: () => setModalTypeOpen('rename') }
      ]
    }
  ]

  return (
    <>
      <TableRow data-testid="key-row">
        <TableCell>{apiKey.name}</TableCell>
        <TableCell>{apiKey.type === 'secret' ? t('typography.secretKey') : t('typography.publicKey')}</TableCell>
        <TableCell>{getFormattedDate(apiKey.createdAt)}</TableCell>
        <TableCell>
          {apiKey.expiresAt ? (
            getFormattedDate(apiKey.expiresAt)
          ) : (
            <Badge variant="default">{t('status.notAvailable')}</Badge>
          )}
        </TableCell>
        <TableCell>
          <Badge variant={badgeVariant}>{badgeText}</Badge>
        </TableCell>
        <TableCell className={clsx(apiKey.type === 'secret' ? styles.blurCellStyle : '', styles.tokenCell)}>
          {displayKey}
        </TableCell>
        <TableCell>{apiKey.type !== 'secret' && <Copy text={apiKey.value} message={t('toast.copied')} />}</TableCell>
        <TableCell>
          <Menu
            sections={menuSections}
            title={
              <Flex align="center" justify="center" data-testid="key-row-menu">
                <Icon name="menu-vertical" size={6} />
              </Flex>
            }
          />
        </TableCell>
      </TableRow>
      {apiKey.setId && (
        <>
          {modalTypeOpen === 'expire' && (
            <ExpireKeySetModal
              keySetId={apiKey.setId}
              keySetName={apiKey.name}
              open={modalTypeOpen === 'expire'}
              onClose={() => setModalTypeOpen(null)}
              refetchApiKeys={refetchApiKeys}
            />
          )}

          {modalTypeOpen === 'rename' && (
            <RenameKeySetModal
              keySetId={apiKey.setId}
              keySetName={apiKey.name}
              open={modalTypeOpen === 'rename'}
              onClose={() => setModalTypeOpen(null)}
              refetchApiKeys={refetchApiKeys}
            />
          )}
        </>
      )}
    </>
  )
}
